import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getSingleStudent } from "../../redux/services/studentSlice";
import { convertUtcToIstDateOnly } from "../../common/TimeUtils";
import "react-phone-number-input/style.css";
import SearchBar from "../../common/SearchBar";
import { onExportDetails } from "../../common/PdfFormat";
import { apiInstance } from "../../redux/services/axiosApi";
import authHeader from "../../redux/services/authHeader";

const UserDetail = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const queryStudentId = queryParams.get("studentId");

  const [searchTerm, setSearchTerm] = useState("");
  const [searchData, setSearchData] = useState("");
  const singleUserData = useSelector(
    (state) => state.student?.singleStudentData
  );

  const [attempts, setAttempts] = useState([]);
  const [loadingAttempts, setLoadingAttempts] = useState(false);

  const fetchAttemptHistory = useCallback(async (userId) => {
    setLoadingAttempts(true);
    try {
      const response = await apiInstance.get(`/qa/all?studentFilter=${userId}&page=1&limit=100`, {
        headers: authHeader(),
      });
      if (response && response.data) {
        setAttempts(response.data.contest_type_data || []);
      }
    } catch (error) {
      console.error("Error fetching attempt history:", error);
    } finally {
      setLoadingAttempts(false);
    }
  }, []);

  useEffect(() => {
    if (singleUserData?._id) {
      fetchAttemptHistory(singleUserData._id);
    } else {
      setAttempts([]);
    }
  }, [singleUserData, fetchAttemptHistory]);

  const getUserDataById = useCallback(async () => {
    await dispatch(getSingleStudent(searchData));
  }, [dispatch, searchData]);

  useEffect(() => {
    if (queryStudentId) {
      setSearchTerm(queryStudentId);
      setSearchData(queryStudentId);
    }
  }, [queryStudentId]);

  useEffect(() => {
    if (searchData) {
      getUserDataById();
    }
  }, [searchData, getUserDataById]);

    const userFields = [
      {
        label: "Name",
        value: singleUserData?.firstName + " " + singleUserData?.lastName,
      },
      { label: "Email", value: singleUserData?.email },
      { label: "DOB", value: convertUtcToIstDateOnly(singleUserData?.dob) },
      {
        label: "Phone Number",
        value: singleUserData?.contact
          ? singleUserData?.contact?.countryCode +
            " " +
            singleUserData?.contact?.mobile
          : "",
      },
      { label: "Gender", value: singleUserData?.gender },
      { label: "City", value: singleUserData?.city },
      { label: "Referral Code", value: singleUserData?.referralCode },
      {
        label: "Friend Referral Code",
        value: singleUserData?.friendReferralCode,
      },
      {
        label: "Referral Code Amount",
        value:
          singleUserData?.refferalCodeAmount > 0
            ? singleUserData?.refferalCodeAmount
            : "",
      },
      {
        label: "Wallet Balance",
        value:
          singleUserData?.walletBalance > 0 ? singleUserData?.walletBalance : "",
      },
    ];

    const onExport = () => {
      onExportDetails(userFields);
    };

  return (
    <div className="my-3">
      <div className="bg-card shadow-theme px-6 py-4 rounded-xl mb-6">
        <div className="flex justify-between flex-wrap">
          <SearchBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            setSearchData={setSearchData}
          />
          {singleUserData && (
            <button
              className="bg-primary text-white px-4 py-2 rounded-xl"
              onClick={onExport}
            >
              Export PDF
            </button>
          )}
        </div>
      </div>

      {singleUserData && (
        <>
          <div className="bg-card shadow-theme px-6 py-8 rounded-xl mb-6">
            <h2 className="md:text-3xl text-2xl font-bold text-foreground text-center">
              Student Details
            </h2>
            <hr className="md:my-7 my-4" />
            <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 md:gap-6 gap-3">
              <div className="col-span-1 md:col-span-2 lg:col-span-3 flex justify-center">
                {singleUserData?.profileImage &&
                  singleUserData?.profileImage !== "string" &&
                  singleUserData?.profileImage.startsWith("http") && (
                    <img
                      src={singleUserData?.profileImage}
                      alt="Profile"
                      className="h-36 w-36 rounded-full border-4 border-primary shadow-theme"
                    />
                  )}
              </div>

              {userFields.map((field, index) =>
                field.value ? (
                  <div key={index} className="md:text-[16px]">
                    <span className="font-bold">{field.label}:</span> 
                    {field.value}
                  </div>
                ) : null
              )}
            </div>
          </div>

          <div className="bg-card shadow-theme px-6 py-8 rounded-xl">
            <h3 className="text-2xl font-bold text-foreground mb-4 border-b pb-4">Attempt History & Analytics</h3>
            {loadingAttempts ? (
              <div className="text-center py-8 text-muted-foreground font-semibold">Loading attempt history...</div>
            ) : attempts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground font-semibold">No attempts found for this student.</div>
            ) : (
              <div className="overflow-x-auto border border-border rounded-xl shadow-sm">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Exam / Contest</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Type / Mode</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Marks / Points</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Accuracy</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Rank</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Winnings</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Date & Time</th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-gray-200">
                    {attempts.map((attempt) => {
                      const totalAnswersCount = (attempt.totalRightAnswer || 0) + (attempt.totalWrongAnswer || 0);
                      const accuracy = totalAnswersCount > 0 
                        ? ((attempt.totalRightAnswer / totalAnswersCount) * 100).toFixed(1) + "%"
                        : "0%";
                      return (
                        <tr key={attempt._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-foreground">
                              {attempt.contestId?.name || "Topic-Wise Practice / Custom"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              attempt.isPractice 
                                ? "bg-blue-100 text-primary" 
                                : "bg-green-100 text-green-800"
                            }`}>
                              {attempt.isPractice 
                                ? `Practice (Attempt ${attempt.attemptNumber || 2})` 
                                : `Live Exam (Attempt 1)`
                              }
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-foreground font-semibold">{attempt.totalPoints || 0} pts</div>
                            <div className="text-xs text-muted-foreground">
                              Correct: {attempt.totalRightAnswer || 0} | Wrong: {attempt.totalWrongAnswer || 0} | Skipped: {attempt.totalSkippedAnswer || 0}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-semibold text-foreground">{accuracy}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                            {attempt.rank ? `#${attempt.rank}` : "—"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground font-semibold">
                            {attempt.winningPrice > 0 ? `₹${attempt.winningPrice}` : "—"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                            {attempt.createdAt ? new Date(attempt.createdAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default UserDetail;
